import React from 'react';
import styled from 'styled-components';
import { ButtonColor } from '../button/Button';
import LinkButton from '../linkButton/LinkButton';
import Popup from '../popup/Popup';
import SLink from '../styled/SLink';

// ############################ Styles ###########################
// tslint:disable:variable-name

const SActionsSection = styled.section<{}>(
  ({ theme }) => `
  display: flex;
  justify-content: flex-end;
  font-size: ${theme.font.size.small};

  @media only screen and (min-width: 800px) {
    font-size: ${theme.font.size.smallMedium2};
  }
`
);

const SPopupSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  &:focus {
    outline-color: transparent;
  }
`;

const STitle = styled.h3<{}>(
  ({ theme }) => `
  color: ${theme.colors.grayDark};
  margin-bottom: ${theme.spacing.larger}px;
  font-size: ${theme.font.size.medium};

  @media only screen and (min-width: 800px) {
    font-size: ${theme.font.size.medium3};
  }
`
);

const SContentText = styled.div<{}>(
  ({ theme }) => `
  font-size: ${theme.font.size.smallMedium2};
  font-weight: ${theme.font.weight.light};
  color: ${theme.colors.grayDark};
  text-align: 'justify';
  margin-bottom: ${theme.spacing.larger}px;
  display: 'flex';
  flex-grow:2;

  @media only ${theme.device.laptop} and (screen) {
    margin-bottom: ${theme.spacing.huger}px;
    font-size: ${theme.font.size.medium};
  }
`
);

const SLinkButton2 = styled(LinkButton)<{}>(
  ({ theme }) => `
  margin-left: ${theme.spacing.large}px;
`
);

// tslint:enable:variable-name
// ############################ End Styles ###########################

interface IDialogAction {
  text: string;
  value: string | number;
  color?: ButtonColor;
}

export interface IAction {
  onClick: (event: any) => void;
  label: string;
  color?: ButtonColor;
}

export interface IDialogProps {
  onAction?: (value: string | number) => void;
  title?: string;
  action1?: IDialogAction;
  action2?: IDialogAction;
  isCloseAppModal?: boolean;
  actionList?: IAction[];
  ratesAndTermsUrl?: string;
}

// tslint:disable-next-line:variable-name
const Dialog: React.FC<IDialogProps> = (props) => {
  const popupContainer = React.useRef<HTMLDivElement>(null);

  const handleAction = React.useCallback(
    (value: string | number) => () => {
      if (props.onAction) {
        props.onAction(value);
      }
    },
    [props.onAction]
  );

  const onClose = () => {
    if (props.isCloseAppModal && props.onAction) {
      props.onAction!(0);
    } else if (props.action1) {
      handleAction(props.action1.value);
    }
  };

  React.useEffect(() => {
    if (popupContainer && popupContainer.current) {
      popupContainer.current.focus();
    }
  }, []);

  return (
    <Popup onClose={onClose}>
      <SPopupSection ref={popupContainer} data-at="dialogModal">
        <STitle tabIndex={0} data-at="dialogTitle">
          {props.title}{' '}
          {props.ratesAndTermsUrl && (
            <SLink data-at="ratesAndTermsLink" key="rateAndTerms" href={props.ratesAndTermsUrl} target="_blank">
              Rates & Terms
            </SLink>
          )}
        </STitle>
        <SContentText tabIndex={0} data-at="dialogText">
          {props.children}
        </SContentText>
        <SActionsSection>
          {props.action1 && (
            <LinkButton bold dataAt="actionButton1" color={props.action1.color || ButtonColor.light} onClick={handleAction(props.action1.value)}>
              {props.action1.text.toUpperCase()}
            </LinkButton>
          )}
          {props.action2 && (
            <>
              <div style={{ width: '20px' }} />
              <LinkButton bold dataAt="actionButton2" color={props.action2.color || ButtonColor.light} onClick={handleAction(props.action2.value)}>
                {props.action2.text.toUpperCase()}
              </LinkButton>
            </>
          )}
          {props.actionList &&
            props.actionList.map((action: IAction, index: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <SLinkButton2 key={`actionButton${index}`} bold dataAt={`actionButton${index}`} color={action.color || ButtonColor.light} onClick={action.onClick}>
                {action.label.toUpperCase()}
              </SLinkButton2>
            ))}
        </SActionsSection>
      </SPopupSection>
    </Popup>
  );
};

export default Dialog;
